import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import unifiedAuth from '../../utils/unifiedAuth';
import { config } from '../../config';

/**
 * Who made this — the cast, director, producer and crew.
 *
 * Until now the player showed `authorName`, which is the admin account that uploaded
 * the file. The people who actually made the film were recorded in `ProductionCredit`
 * and shown to nobody, so the platform stored who directed something and displayed who
 * published it.
 *
 * For a production company that is the wrong way round. A credit is the product of the
 * work for the people in it — an actor's reel, a crew member's next job.
 */

interface CreditEntry {
  name: string;
  role: string | null;
  lead: boolean;
  /** Present only when the credited person is in the community. */
  memberSlug: string | null;
}

interface CreditGroup {
  department: string;
  credits: CreditEntry[];
}

/** CAST becomes Cast, HAIR_MAKEUP becomes Hair & Makeup. */
const DEPARTMENT_LABELS: Record<string, string> = {
  CAST: 'Cast',
  DIRECTING: 'Directing',
  WRITING: 'Writing',
  PRODUCING: 'Producing',
  CAMERA: 'Camera',
  SOUND: 'Sound',
  MUSIC: 'Music',
  EDITING: 'Editing',
  ART: 'Art',
  COSTUME: 'Costume',
  HAIR_MAKEUP: 'Hair & Makeup',
  VFX_ANIMATION: 'VFX & Animation',
  STUNTS: 'Stunts',
  CASTING: 'Casting',
  PRODUCTION: 'Production',
  OTHER: 'Other',
};

const label = (department: string) =>
  DEPARTMENT_LABELS[department] ??
  // A department added to the backend enum but not here still reads sensibly, rather
  // than showing HAIR_MAKEUP to an audience.
  department.charAt(0) + department.slice(1).toLowerCase().replace(/_/g, ' ');

// Cast lists run long. Showing everything by default buries the description under
// forty names; showing too few makes the section look like an afterthought.
const COLLAPSED_LIMIT = 8;

const ContentCredits = ({ contentId }: { contentId: string }) => {
  const [groups, setGroups] = useState<CreditGroup[]>([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // No loading state and no error state, deliberately. Most content has no credits,
    // so the common outcome is an empty section — and a spinner or an error where
    // nothing was ever going to appear is worse than silence.
    unifiedAuth
      .fetch(`${config.apiBaseUrl}/content/${contentId}/credits`)
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (cancelled || !payload) return;
        setGroups(payload?.data?.departments ?? payload?.departments ?? []);
      })
      .catch(() => {
        /* Credits are enrichment. Failing to load them must not disturb playback. */
      });

    return () => {
      cancelled = true;
    };
  }, [contentId]);

  if (groups.length === 0) return null;

  const total = groups.reduce((n, g) => n + g.credits.length, 0);
  const showAll = expanded || total <= COLLAPSED_LIMIT;

  let remaining = COLLAPSED_LIMIT;
  const visible = showAll
    ? groups
    : groups
        .map((g) => {
          const take = Math.max(0, Math.min(remaining, g.credits.length));
          remaining -= take;
          return { ...g, credits: g.credits.slice(0, take) };
        })
        .filter((g) => g.credits.length > 0);

  return (
    <div className="mt-6 border-t border-gray-800 pt-6">
      <h3 className="text-lg font-semibold text-white mb-4">Credits</h3>

      <div className="space-y-4">
        {visible.map((group) => (
          <div key={group.department}>
            <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-2">
              {label(group.department)}
            </h4>
            <ul className="space-y-1">
              {group.credits.map((credit, i) => (
                <li key={`${credit.name}-${i}`} className="text-sm">
                  {credit.memberSlug ? (
                    // Credited community members link to their profile. This is the
                    // community half of the platform doing something for them.
                    <Link
                      to={`/community/members/${credit.memberSlug}`}
                      className="text-orange-400 hover:text-orange-300 hover:underline"
                    >
                      {credit.name}
                    </Link>
                  ) : (
                    <span className="text-gray-200">{credit.name}</span>
                  )}
                  {credit.role && <span className="text-gray-500"> — {credit.role}</span>}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {!showAll && (
        <button
          onClick={() => setExpanded(true)}
          className="mt-4 text-sm text-orange-400 hover:text-orange-300"
        >
          Show all {total} credits
        </button>
      )}
    </div>
  );
};

export default ContentCredits;
